﻿import { htmlEncode, indexOf, tryGetText } from "@serenity-is/corelib/q";
import { GroupInfo } from "@serenity-is/corelib/slick";
import { Column, EventEmitter, Grid, Group, NonDataRow } from "@serenity-is/sleekgrid";

declare module "@serenity-is/sleekgrid" {
    interface Column<TItem = any> {
        grouping?: false | GroupInfo<any>;
    }
}

export interface DraggableGroupingOptions {
    deleteIconCssClass?: string;
    deleteIconImage?: string;
    dropPlaceHolderText?: string;
    getGroupingFor?: (column: Column) => GroupInfo<any>;
    getAllColumns?: () => Column[];
    getCount?: (group: Group<any>) => number;
    displayTotalsRow?: boolean;
    collapsed?: boolean;
}

export interface GroupChangeInfo {
    groupingArray: any[];
    userChange: boolean;
}

/**
 * Based on plugin at https://github.com/muthukumarse/Slickgrid
 */
export class DraggableGroupingPlugin {

    private grid: Grid;
    private gridUid: string;
    private dropbox: JQuery;
    private dropboxPlaceholder: JQuery;
    private expandAll: JQuery;
    private collapseAll: JQuery;
    private options: DraggableGroupingOptions;
    private columnsGroupBy: Column[];
    public readonly onGroupChanged = new EventEmitter<GroupChangeInfo>();

    constructor(options?: DraggableGroupingOptions) {
        var defaults: DraggableGroupingOptions = {
            deleteIconCssClass: 'fa fa-cross'
        };

        this.options = $.extend(true, {}, defaults, options);
    }

    public init(grid: Grid) {
        this.grid = grid;
        this.gridUid = grid['getUID']();

        this.dropbox = $(grid['getGroupingPanel']());
        var dropPlaceHolderText = this.options.dropPlaceHolderText ||
            tryGetText("Site.DraggableGroupingMixin.DropPlaceholder") ||
            "Drag a column header here to group by that column";

        this.dropbox.html("<div class='slick-dropped-placeholder'>" +
            htmlEncode(dropPlaceHolderText) + "</div>" +
            `<div class="slick-group-expand-all" title="${htmlEncode(tryGetText("Site.DraggableGrupingMixin.ExpandAllButton") || "Expand All")}" style="display:none"></div>` +
            `<div class="slick-group-collapse-all" title="${htmlEncode(tryGetText("Site.DraggableGrupingMixin.CollapseAllButton") || "Collapse All")}" style="display:none"></div>`);

        this.dropboxPlaceholder = this.dropbox.find(".slick-dropped-placeholder");
        this.expandAll = this.dropbox.find(".slick-group-expand-all");
        this.collapseAll = this.dropbox.find(".slick-group-collapse-all");
        this.setupColumnDropbox();

        var cols = this.getColumns();
        for (var i = 0; i < cols.length; i++) {
            var col = cols[i];
            grid['updateColumnHeader'](col.id);
        }

        this.columnsGroupBy = [];
    }

    private getColumns(): Column[] {
        return (this.options.getAllColumns && this.options.getAllColumns()) ||
            this.grid.getColumns();
    }

    public destroy() {
        this.onGroupChanged.clear();
    }

    private setupColumnDropbox() {
        var _self = this;
        this.dropbox.droppable({
            activeClass: "ui-state-default",
            hoverClass: "ui-state-hover",
            accept: ":not(.ui-sortable-helper)",
            deactivate: (event, ui) => {
                this.dropbox.removeClass("slick-header-column-denied");
            },
            drop: (_, ui) => {
                this.addGroupColumn(ui.draggable.attr('id').replace(_self.gridUid, "")) &&
                    this.groupChanged(true);
            },
            over: (_, ui) => {
                var id = (ui.draggable).attr('id').replace(this.gridUid, "");
                this.getColumns().forEach((e, i, a) => {
                    if (e.id == id) {
                        if (e.grouping !== false) {
                            this.dropbox.addClass("slick-header-column-denied");
                        }
                    }
                });
            }
        });

        this.dropbox.sortable({
            items: "div.slick-dropped-grouping",
            cursor: "default",
            tolerance: "pointer",
            helper: "clone",
            update: (event, ui) => {
                var sortArray = $(event.target).sortable('toArray', {
                    attribute: 'data-id'
                }),
                    newGroupingOrder = [];
                for (var i = 0, l = sortArray.length; i < l; i++) {
                    for (var a = 0, b = this.columnsGroupBy.length; a < b; a++) {
                        if (this.columnsGroupBy[a].id == sortArray[i]) {
                            newGroupingOrder.push(this.columnsGroupBy[a]);
                            break;
                        }
                    }
                }
                this.columnsGroupBy = newGroupingOrder;
                this.groupChanged(true);
            }
        });

        this.expandAll.on('click', (e) => {
            (this.grid.getData() as any).expandAllGroups();
        });

        this.collapseAll.on('click', (e) => {
            (this.grid.getData() as any).collapseAllGroups();
        });
    }

    public getGroupingFor(column: Column): GroupInfo<any> {
        var g = null;

        if (this.options.getGroupingFor) {
            g = this.options.getGroupingFor(column);
        }

        if (g === false)
            return g;

        var fld = column.field || column.id;
        var asc = 1;
        var s = this.grid.getSortColumns();
        if (s != null && s.length > 0) {
            for (var x of s) {
                if (x.columnId == column.id) {
                    asc = x.sortAsc ? 1 : -1;
                    break;
                }
            }
        }

        return $.extend({
            getter: fld,
            displayTotalsRow: this.options.displayTotalsRow,
            comparer: function (a, b) {
                return (a.value === b.value ? 0 :
                    (((a.value != null && b.value == null) || (a.value > b.value)) ? asc : -asc));
            },
            formatter: (g: Group<any>) => {
                var text;
                if (column.format || column.formatter) {
                    var format = this.grid.getFormatter(-1, column);
                    if (format) {
                        var item = new NonDataRow();
                        var ctx = this.grid.getFormatterContext(-1, -1);
                        item["__grp"] = g;
                        item[column.field] = g.value;
                        ctx.item = item;
                        ctx.value = g.value;
                        try {
                            text = format(ctx);
                        }
                        catch (e) {
                            text = htmlEncode(g.value);
                        }
                    }
                    else
                        text = htmlEncode(g.value);
                }
                else
                    text = htmlEncode(g.value);

                var count = this.options.getCount ? this.options.getCount(g) : g.count;
                return '<span class="s-group-name">' +
                    (column.name || column.field || column.id) + ': </span>' +
                    text + ' <span class="s-group-count">(' +
                    (count || 0).toString() + ")</span>";
            },
            collapsed: true
        }, g);
    }

    private addGroupColumn(columnid: string): boolean {
        var columnAllowed = true;
        this.columnsGroupBy.forEach(function (clmn, i, a) {
            if (clmn.id == columnid) {
                columnAllowed = false;
            }
        });

        var gridColumns = this.getColumns();

        if (!columnAllowed)
            return;

        var columnAdded = false;
        gridColumns.forEach((col, i, a) => {
            if (col.id !== columnid || col.grouping === false)
                return;

            if (col.grouping == null || $.isEmptyObject(col.grouping)) {
                col.grouping = this.getGroupingFor(col);
                if (col.grouping as any === false)
                    return;
            }

            var entry = $("<div id='" + this.gridUid +
                col.id + "_entry' data-id='" + col.id +
                "' class='slick-dropped-grouping'>");

            var groupText = $("<div style='display: inline-flex'>" +
                htmlEncode(col.name) + "</div>")

            groupText.appendTo(entry);

            var groupRemoveIcon = $("<div class='slick-groupby-remove'></div>");
            if (this.options.deleteIconCssClass)
                groupRemoveIcon.addClass(this.options.deleteIconCssClass);

            if (this.options.deleteIconImage)
                groupRemoveIcon.css("background", "url(" + this.options.deleteIconImage + ") no-repeat center right");

            if (!this.options.deleteIconCssClass && !this.options.deleteIconImage)
                groupRemoveIcon.addClass('slick-groupby-remove-image');

            groupRemoveIcon.appendTo(entry);

            $("</div>").appendTo(entry);
            entry.appendTo(this.dropbox);

            this.columnsGroupBy.push(col);
            groupRemoveIcon.one('click', () => this.removeIconClick(col.id, entry));
            columnAdded = true;
        });

        if (columnAdded) {
            this.grid.invalidate();
            return true;
        }
    }

    public setDroppedGroups(idList: string[]) {
        this.columnsGroupBy = [];
        this.dropbox.find(".slick-dropped-grouping").remove();
        if (!idList || !idList.length)
            return;
        for (var i = 0; i < idList.length; i++) {
            this.addGroupColumn(idList[i]);
        }
        this.groupChanged(false);
    }

    private removeIconClick(id: string, entry: JQuery) {
        entry.remove();
        var changed = false;
        var idx;
        while ((idx = indexOf(this.columnsGroupBy, x => x.id == id)) >= 0) {
            this.columnsGroupBy.splice(idx, 1);
            changed = true;
        }
        changed && this.groupChanged(true);
    }

    private updateInterface() {
        var hasGrouping = !!(this.columnsGroupBy && this.columnsGroupBy.length);
        this.collapseAll.toggle(hasGrouping);
        this.expandAll.toggle(hasGrouping);
        this.dropboxPlaceholder.toggle(!hasGrouping);
    }

    private groupChanged(userChange: boolean) {
        this.updateInterface();

        if (this.columnsGroupBy.length == 0) {
            (this.grid.getData() as any).setGrouping([]);
            this.onGroupChanged.notify({ groupColumns: [], userChange });
            this.grid.invalidate();
            return;
        }

        var groupingArray: any[] = [];
        this.columnsGroupBy.forEach(function (element) {
            groupingArray.push(element.grouping);
        });

        (this.grid.getData() as any).setGrouping(groupingArray);
        this.grid.invalidate();
        this.onGroupChanged.notify({ groupColumns: groupingArray, userChange });
    }

    public static setupColumnReorder(grid: Grid, $headers: JQuery,
        setupColumnResize: () => void, trigger: (ev: any, p: any) => void) {
        $headers.filter(":ui-sortable").sortable("destroy");
        var $headerDraggableGroupBy = $(grid['getGroupingPanel']());
        var columns = grid.getColumns();
        var uid = grid['getUID']();
        $headers.sortable({
            distance: 3,
            cursor: "default",
            tolerance: "intersection",
            helper: "clone",
            placeholder: "slick-sortable-placeholder ui-state-default slick-header-column",
            forcePlaceholderSize: true,
            appendTo: "body",
            start: function (e, ui) {
                $(ui.helper).addClass("slick-header-column-active");
            },
            beforeStop: function (e, ui) {
                $(ui.helper).removeClass("slick-header-column-active");
                var hasDroppedColumn = $headerDraggableGroupBy
                    .find(".slick-dropped-grouping").length;
                if (hasDroppedColumn > 0) {
                    $headerDraggableGroupBy.find(".slick-dropped-placeholder").hide();
                    $headerDraggableGroupBy.find(".slick-dropped-grouping").show();
                }
            },
            stop: function (e) {
                if (!grid['getEditorLock']().commitCurrentEdit()) {
                    $(this).sortable("cancel");
                    return;
                }

                var reorderedIds = $headers.sortable("toArray");
                var reorderedColumns = [];
                for (var i = 0; i < reorderedIds.length; i++) {
                    reorderedColumns.push(columns[grid.getColumnIndex(
                        reorderedIds[i].replace(uid, ""))]);
                }
                grid.setColumns(reorderedColumns);
                trigger(grid.onColumnsReordered, {});
                e.stopPropagation();
                setupColumnResize();
            }
        });
    }
}

typeof globalThis != "undefined" && globalThis["Slick"] && globalThis["Slick"]["Plugins"] && (globalThis.Slick["Plugins"]["DraggableGrouping"] = DraggableGroupingPlugin);